import { useState } from 'react';
import {
  Archive,
  Check,
  CheckSquare,
  Code2,
  Download,
  FileText,
  Link as LinkIcon,
  Plus,
  Quote,
  Square,
  Star,
  Trash2,
} from 'lucide-react';
import {
  useLousas,
  useLousa,
  useCreateLousa,
  useUpdateLousa,
  useDeleteLousa,
  useAddBlock,
  useUpdateBlock,
  useDeleteBlock,
  type BlockTipo,
  type Lousa,
  type LousaBlock,
} from '@/hooks/useLousas';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useToast } from '@/lib/toast';

const BLOCK_ICONS: Record<BlockTipo, typeof FileText> = {
  markdown: FileText,
  todo: CheckSquare,
  quote: Quote,
  code: Code2,
  link: LinkIcon,
};

export function LousasPage(): JSX.Element {
  const [showArquivadas, setShowArquivadas] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [newTitulo, setNewTitulo] = useState('');

  const { data, isLoading } = useLousas({ arquivadas: showArquivadas });
  const create = useCreateLousa();
  const toast = useToast();

  const lousas = data?.lousas ?? [];
  const stats = data?.stats;

  async function handleCreate(): Promise<void> {
    if (!newTitulo.trim()) return;
    const lousa = await create.mutateAsync({ titulo: newTitulo.trim() });
    setNewTitulo('');
    setSelectedSlug(lousa.slug);
    toast.success('Lousa criada', lousa.titulo);
  }

  return (
    <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-[380px_1fr]">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-2xl font-semibold">Lousas</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {stats ? `${stats.ativas} ativas · ${stats.total_blocks} blocks` : '—'}
            </p>
          </div>
          <Button
            size="sm"
            variant={showArquivadas ? 'default' : 'outline'}
            onClick={() => setShowArquivadas((v) => !v)}
          >
            <Archive className="mr-1 h-3 w-3" />
            {showArquivadas ? 'Arquivadas' : 'Ativas'}
          </Button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
          className="flex gap-2"
        >
          <Input
            placeholder="Nova lousa (título)..."
            value={newTitulo}
            onChange={(e) => setNewTitulo(e.target.value)}
          />
          <Button type="submit" disabled={!newTitulo.trim() || create.isPending}>
            <Plus className="h-4 w-4" />
          </Button>
        </form>

        <div className="space-y-2">
          {isLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
          {!isLoading && lousas.length === 0 && (
            <Card>
              <CardContent className="py-6 text-center text-sm text-muted-foreground">
                {showArquivadas
                  ? 'Nenhuma lousa arquivada'
                  : 'Crie sua primeira lousa — anotações rápidas, to-dos, trechos de código.'}
              </CardContent>
            </Card>
          )}
          {lousas.map((l) => (
            <LousaCard
              key={l.id}
              lousa={l}
              selected={selectedSlug === l.slug}
              onClick={() => setSelectedSlug(l.slug)}
            />
          ))}
        </div>
      </div>

      <div>
        {selectedSlug ? (
          <LousaDetail
            slug={selectedSlug}
            onDeleted={() => setSelectedSlug(null)}
          />
        ) : (
          <Card>
            <CardContent className="py-16 text-center text-sm text-muted-foreground">
              Selecione uma lousa à esquerda ou crie uma nova.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function LousaCard({
  lousa,
  selected,
  onClick,
}: {
  lousa: Lousa;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border bg-card px-3 py-2 text-left transition hover:border-primary/40',
        selected && 'border-primary bg-primary/5',
      )}
    >
      <div className="flex items-center gap-2">
        {lousa.fixada === 1 && <Star className="h-3 w-3 fill-amber-400 text-amber-500" />}
        <span className="font-semibold">{lousa.titulo}</span>
      </div>
      {lousa.descricao && (
        <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{lousa.descricao}</p>
      )}
      <div className="mt-1 text-[10px] text-muted-foreground">
        {new Date(lousa.atualizado_em).toLocaleDateString('pt-BR')}
      </div>
    </button>
  );
}

function LousaDetail({
  slug,
  onDeleted,
}: {
  slug: string;
  onDeleted: () => void;
}): JSX.Element {
  const { data: lousa, isLoading } = useLousa(slug);
  const update = useUpdateLousa();
  const del = useDeleteLousa();
  const addBlock = useAddBlock();
  const toast = useToast();

  if (isLoading || !lousa) {
    return (
      <Card>
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          Carregando...
        </CardContent>
      </Card>
    );
  }

  async function handleFixar(): Promise<void> {
    if (!lousa) return;
    await update.mutateAsync({
      slug: lousa.slug,
      patch: { fixada: lousa.fixada === 0 },
    });
  }

  async function handleArquivar(): Promise<void> {
    if (!lousa) return;
    await update.mutateAsync({
      slug: lousa.slug,
      patch: { arquivada: lousa.arquivada === 0 },
    });
    toast.info(lousa.arquivada === 0 ? 'Arquivada' : 'Desarquivada', lousa.titulo);
  }

  async function handleDelete(): Promise<void> {
    if (!lousa) return;
    if (!confirm(`Excluir "${lousa.titulo}" e todos os blocks?`)) return;
    await del.mutateAsync(lousa.slug);
    onDeleted();
    toast.success('Lousa excluída', lousa.titulo);
  }

  async function handleAddBlock(tipo: BlockTipo): Promise<void> {
    if (!lousa) return;
    await addBlock.mutateAsync({
      slug: lousa.slug,
      block: { tipo, conteudo: tipo === 'code' ? '// ...' : tipo === 'todo' ? 'Nova tarefa' : '' },
    });
  }

  async function handleExport(): Promise<void> {
    if (!lousa) return;
    const res = await fetch(`/api/lousas/${lousa.slug}/export`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken') ?? ''}` },
    });
    const md = await res.text();
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${lousa.slug}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exportado', `${lousa.slug}.md`);
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <Input
              value={lousa.titulo}
              onChange={(e) =>
                update.mutate({ slug: lousa.slug, patch: { titulo: e.target.value } })
              }
              className="border-none bg-transparent p-0 text-xl font-bold shadow-none focus-visible:ring-0"
            />
            <Textarea
              placeholder="Descrição opcional..."
              value={lousa.descricao ?? ''}
              onChange={(e) =>
                update.mutate({ slug: lousa.slug, patch: { descricao: e.target.value } })
              }
              className="mt-1 min-h-0 border-none bg-transparent p-0 text-sm text-muted-foreground shadow-none focus-visible:ring-0"
              rows={1}
            />
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={handleFixar} title="Fixar">
              <Star
                className={cn(
                  'h-4 w-4',
                  lousa.fixada === 1 && 'fill-amber-400 text-amber-500',
                )}
              />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleExport} title="Export MD">
              <Download className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleArquivar} title="Arquivar">
              <Archive className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDelete}
              title="Excluir"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {lousa.blocks.map((b) => (
            <BlockRow key={b.id} block={b} slug={lousa.slug} />
          ))}
        </div>

        <div className="flex flex-wrap gap-2 border-t pt-4">
          <span className="text-xs text-muted-foreground self-center">+ bloco:</span>
          {(['markdown', 'todo', 'quote', 'code', 'link'] as const).map((t) => {
            const Icon = BLOCK_ICONS[t];
            return (
              <Button
                key={t}
                size="sm"
                variant="outline"
                onClick={() => handleAddBlock(t)}
                disabled={addBlock.isPending}
              >
                <Icon className="mr-1 h-3 w-3" />
                {t}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function BlockRow({ block, slug }: { block: LousaBlock; slug: string }): JSX.Element {
  const update = useUpdateBlock();
  const del = useDeleteBlock();
  const Icon = BLOCK_ICONS[block.tipo];
  let meta: { completado?: boolean; linguagem?: string; url?: string } = {};
  if (block.metadata_json) {
    try {
      meta = JSON.parse(block.metadata_json);
    } catch {
      meta = {};
    }
  }

  if (block.tipo === 'todo') {
    const completado = meta.completado === true;
    return (
      <div className="flex items-start gap-2 rounded-md border bg-card px-3 py-2">
        <button
          type="button"
          className="mt-0.5"
          onClick={() =>
            update.mutate({
              id: block.id,
              slug,
              patch: { metadata: { ...meta, completado: !completado } },
            })
          }
        >
          {completado ? (
            <Check className="h-4 w-4 text-emerald-600" />
          ) : (
            <Square className="h-4 w-4" />
          )}
        </button>
        <Textarea
          value={block.conteudo}
          onChange={(e) => update.mutate({ id: block.id, slug, patch: { conteudo: e.target.value } })}
          className={cn(
            'min-h-0 flex-1 border-none bg-transparent p-0 shadow-none focus-visible:ring-0',
            completado && 'line-through opacity-60',
          )}
          rows={1}
        />
        <Button size="sm" variant="ghost" onClick={() => del.mutate({ id: block.id, slug })}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  if (block.tipo === 'link') {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Título"
          value={block.conteudo}
          onChange={(e) => update.mutate({ id: block.id, slug, patch: { conteudo: e.target.value } })}
          className="flex-1"
        />
        <Input
          placeholder="https://..."
          value={meta.url ?? ''}
          onChange={(e) =>
            update.mutate({
              id: block.id,
              slug,
              patch: { metadata: { ...meta, url: e.target.value } },
            })
          }
          className="flex-1"
        />
        <Button size="sm" variant="ghost" onClick={() => del.mutate({ id: block.id, slug })}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  const language =
    block.tipo === 'code' ? meta.linguagem ?? '' : undefined;

  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="flex items-center justify-between pb-1">
        <div className="flex items-center gap-2">
          <Icon className="h-3 w-3 text-muted-foreground" />
          <Badge variant="outline" className="text-[10px]">
            {block.tipo}
          </Badge>
          {block.tipo === 'code' && (
            <Input
              placeholder="linguagem"
              value={language ?? ''}
              onChange={(e) =>
                update.mutate({
                  id: block.id,
                  slug,
                  patch: { metadata: { ...meta, linguagem: e.target.value } },
                })
              }
              className="h-6 w-24 text-xs"
            />
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => del.mutate({ id: block.id, slug })}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      <Textarea
        value={block.conteudo}
        onChange={(e) => update.mutate({ id: block.id, slug, patch: { conteudo: e.target.value } })}
        className={cn(
          'min-h-[60px] border-none bg-transparent p-0 shadow-none focus-visible:ring-0',
          block.tipo === 'code' && 'font-mono text-xs',
          block.tipo === 'quote' && 'italic text-muted-foreground',
        )}
        rows={3}
      />
    </div>
  );
}
